 
 
import { Payload } from ".";
import { AppDispatch } from "../../configureStore";
import { ConnectionStatus, HealthCheckResponse, InFlightMessage, InFlightResponse, LogsResponse, Receiver, ReceiverActionResponse, ReceiversResponse } from "../../interface/returnOps";
import execute from "../http/execute";
import failure from "../http/failure";
import { getReturnOps, postReturnOps } from "../http/request";
import success from "../http/success";

export type ReturnOpsState = {
    healthStatus?: HealthCheckResponse;
    receivers: Receiver[];
    logs: LogsResponse | null;
    loadingHealth: boolean;
    loadingReceivers: boolean;
    loadingLogs: boolean;
    inFlightMessages: InFlightMessage[];
    inFlightConnectionStatus: ConnectionStatus;
    loadingInFlight: boolean;
    error?: string;
};

const initialState: ReturnOpsState = {
    receivers: [],
    logs: null,
    loadingHealth: false,
    loadingReceivers: false,
    loadingLogs: false,
    inFlightMessages: [],
    inFlightConnectionStatus: 'disconnected',
    loadingInFlight: false,
};

export const RESET_RETURN_OPS = 'returnOps/RESET_RETURN_OPS';

export const HEALTH_CHECK_REQUEST = 'returnOps/HEALTH_CHECK_REQUEST';
export const HEALTH_CHECK_SUCCESS = 'returnOps/HEALTH_CHECK_SUCCESS';
export const HEALTH_CHECK_FAILURE = 'returnOps/HEALTH_CHECK_FAILURE';

export const GET_RECEIVERS_REQUEST = 'returnOps/GET_RECEIVERS_REQUEST';
export const GET_RECEIVERS_SUCCESS = 'returnOps/GET_RECEIVERS_SUCCESS';
export const GET_RECEIVERS_FAILURE = 'returnOps/GET_RECEIVERS_FAILURE';

export const ENABLE_RECEIVER_REQUEST = 'returnOps/ENABLE_RECEIVER_REQUEST';
export const ENABLE_RECEIVER_SUCCESS = 'returnOps/ENABLE_RECEIVER_SUCCESS';
export const ENABLE_RECEIVER_FAILURE = 'returnOps/ENABLE_RECEIVER_FAILURE';

export const DISABLE_RECEIVER_REQUEST = 'returnOps/DISABLE_RECEIVER_REQUEST';
export const DISABLE_RECEIVER_SUCCESS = 'returnOps/DISABLE_RECEIVER_SUCCESS';
export const DISABLE_RECEIVER_FAILURE = 'returnOps/DISABLE_RECEIVER_FAILURE';

export const GET_LOGS_REQUEST = 'returnOps/GET_LOGS_REQUEST';
export const GET_LOGS_SUCCESS = 'returnOps/GET_LOGS_SUCCESS';
export const GET_LOGS_FAILURE = 'returnOps/GET_LOGS_FAILURE';

export const GET_INFLIGHT_REQUEST = 'returnOps/GET_INFLIGHT_REQUEST';
export const GET_INFLIGHT_SUCCESS = 'returnOps/GET_INFLIGHT_SUCCESS';
export const GET_INFLIGHT_FAILURE = 'returnOps/GET_INFLIGHT_FAILURE';

export const SET_INFLIGHT_MESSAGES = 'returnOps/SET_INFLIGHT_MESSAGES';
export const UPSERT_INFLIGHT_MESSAGE = 'returnOps/UPSERT_INFLIGHT_MESSAGE';
export const REMOVE_INFLIGHT_MESSAGE = 'returnOps/REMOVE_INFLIGHT_MESSAGE';
export const SET_INFLIGHT_CONNECTION_STATUS = 'returnOps/SET_INFLIGHT_CONNECTION_STATUS';

type ActionParams =
    | { type: typeof RESET_RETURN_OPS; }
    | { type: typeof HEALTH_CHECK_REQUEST; }
    | { type: typeof HEALTH_CHECK_SUCCESS; data: HealthCheckResponse; }
    | { type: typeof HEALTH_CHECK_FAILURE; error: string; }
    | { type: typeof GET_RECEIVERS_REQUEST; }
    | { type: typeof GET_RECEIVERS_SUCCESS; data: ReceiversResponse; }
    | { type: typeof GET_RECEIVERS_FAILURE; error: string; }
    | { type: typeof ENABLE_RECEIVER_REQUEST; }
    | { type: typeof ENABLE_RECEIVER_SUCCESS; data: ReceiverActionResponse; }
    | { type: typeof ENABLE_RECEIVER_FAILURE; error: string; }
    | { type: typeof DISABLE_RECEIVER_REQUEST; }
    | { type: typeof DISABLE_RECEIVER_SUCCESS; data: ReceiverActionResponse; }
    | { type: typeof DISABLE_RECEIVER_FAILURE; error: string; }
    | { type: typeof GET_LOGS_REQUEST; }
    | { type: typeof GET_LOGS_SUCCESS; data: LogsResponse; }
    | { type: typeof GET_LOGS_FAILURE; error: string; }
    | { type: typeof GET_INFLIGHT_REQUEST; }
    | { type: typeof GET_INFLIGHT_SUCCESS; data: InFlightResponse; }
    | { type: typeof GET_INFLIGHT_FAILURE; error: string; }
    | { type: typeof SET_INFLIGHT_MESSAGES; data: InFlightMessage[]; }
    | { type: typeof UPSERT_INFLIGHT_MESSAGE; data: InFlightMessage; }
    | { type: typeof REMOVE_INFLIGHT_MESSAGE; messageId: string; }
    | { type: typeof SET_INFLIGHT_CONNECTION_STATUS; status: ConnectionStatus; };

function reducer(state = initialState, action: ActionParams): typeof initialState {
    switch (action.type) {
        case RESET_RETURN_OPS:
            return initialState;

        case HEALTH_CHECK_REQUEST:
            return {
                ...state,
                loadingHealth: true,
                error: undefined,
            };

        case HEALTH_CHECK_SUCCESS:
            return {
                ...state,
                healthStatus: action.data,
                loadingHealth: false,
                error: undefined,
            };

        case HEALTH_CHECK_FAILURE:
            return {
                ...state,
                loadingHealth: false,
                error: action.error,
                healthStatus: undefined,
            };

        case GET_RECEIVERS_REQUEST:
            return {
                ...state,
                loadingReceivers: true,
                error: undefined,
            };

        case GET_RECEIVERS_SUCCESS:
            return {
                ...state,
                receivers: action.data.receivers,
                loadingReceivers: false,
                error: undefined,
            };

        case GET_RECEIVERS_FAILURE:
            return {
                ...state,
                loadingReceivers: false,
                error: action.error,
            };

        case ENABLE_RECEIVER_REQUEST:
        case DISABLE_RECEIVER_REQUEST:
            return {
                ...state,
                error: undefined,
            };

        case ENABLE_RECEIVER_SUCCESS:
        case DISABLE_RECEIVER_SUCCESS:
            return {
                ...state,
                receivers: state.receivers.map((receiver) => (receiver.id === action.data.receiverId
                    ? { ...receiver, enabled: action.data.enabled }
                    : receiver)),
                error: undefined,
            };

        case ENABLE_RECEIVER_FAILURE:
        case DISABLE_RECEIVER_FAILURE:
            return {
                ...state,
                error: action.error,
            };

        case GET_LOGS_REQUEST:
            return {
                ...state,
                loadingLogs: true,
                error: undefined,
            };

        case GET_LOGS_SUCCESS:
            return {
                ...state,
                logs: action.data,
                loadingLogs: false,
                error: undefined,
            };

        case GET_LOGS_FAILURE:
            return {
                ...state,
                loadingLogs: false,
                error: action.error,
            };

        case GET_INFLIGHT_REQUEST:
            return {
                ...state,
                loadingInFlight: true,
            };

        case GET_INFLIGHT_SUCCESS:
            return {
                ...state,
                inFlightMessages: action.data.messages,
                loadingInFlight: false,
            };

        case GET_INFLIGHT_FAILURE:
            return {
                ...state,
                loadingInFlight: false,
                error: action.error,
            };

        case SET_INFLIGHT_MESSAGES:
            return {
                ...state,
                inFlightMessages: action.data,
            };

        case UPSERT_INFLIGHT_MESSAGE: {
            const idx = state.inFlightMessages.findIndex(
                (m) => m.messageId === action.data.messageId,
            );
            if (idx >= 0) {
                const updated = [...state.inFlightMessages];
                updated[idx] = action.data;
                return { ...state, inFlightMessages: updated };
            }
            return {
                ...state,
                inFlightMessages: [...state.inFlightMessages, action.data],
            };
        }

        case REMOVE_INFLIGHT_MESSAGE:
            return {
                ...state,
                inFlightMessages: state.inFlightMessages.filter(
                    (m) => m.messageId !== action.messageId,
                ),
            };

        case SET_INFLIGHT_CONNECTION_STATUS:
            return {
                ...state,
                inFlightConnectionStatus: action.status,
            };

        default:
            return state;
    }
}

export const checkHealth = (payload?: Payload<unknown>) => (dispatch: AppDispatch) => {
    dispatch({
        type: HEALTH_CHECK_REQUEST,
        payload,
    });

    execute(getReturnOps, '/health', payload?.input || {})
        .then(success(dispatch, HEALTH_CHECK_SUCCESS, payload))
        .catch(failure(dispatch, HEALTH_CHECK_FAILURE, payload));
};

export const getReceivers = (payload?: Payload<unknown>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_RECEIVERS_REQUEST,
        payload,
    });

    execute(getReturnOps, '/receivers', payload?.input || {})
        .then(success(dispatch, GET_RECEIVERS_SUCCESS, payload))
        .catch(failure(dispatch, GET_RECEIVERS_FAILURE, payload));
};

export const enableReceiver = (payload: Payload<{ receiverId: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: ENABLE_RECEIVER_REQUEST,
        payload,
    });

    execute(postReturnOps, `/receivers/${payload.input?.receiverId}/enable`, {})
        .then(success(dispatch, ENABLE_RECEIVER_SUCCESS, payload))
        .catch(failure(dispatch, ENABLE_RECEIVER_FAILURE, payload));
};

export const disableReceiver = (payload: Payload<{ receiverId: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: DISABLE_RECEIVER_REQUEST,
        payload,
    });

    execute(postReturnOps, `/receivers/${payload.input?.receiverId}/disable`, {})
        .then(success(dispatch, DISABLE_RECEIVER_SUCCESS, payload))
        .catch(failure(dispatch, DISABLE_RECEIVER_FAILURE, payload));
};

export const getLogs = (payload?: Payload<unknown>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_LOGS_REQUEST,
        payload,
    });

    execute(getReturnOps, '/logs', payload?.input || {})
        .then(success(dispatch, GET_LOGS_SUCCESS, payload))
        .catch(failure(dispatch, GET_LOGS_FAILURE, payload));
};

export const getInFlightMessages = (payload?: Payload<unknown>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_INFLIGHT_REQUEST,
        payload,
    });

    execute(getReturnOps, '/api/inflight', payload?.input || {})
        .then(success(dispatch, GET_INFLIGHT_SUCCESS, payload))
        .catch(failure(dispatch, GET_INFLIGHT_FAILURE, payload));
};

export default reducer;
